import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderGetTagProps,
  Chip,
  FormHelperText,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { omit } from 'lodash-es';
import throttle from 'lodash-es/throttle';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import ILocation from '../../types/ILocation';
import { Label } from '../Label/';
import { WsSelect } from '../WsSelect';
import { WsSelectObj } from '../WsSelect/WsSelect';

import { placeToLocation } from './utils/location';

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }
  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

interface PlaceType {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings: [
      {
        offset: number;
        length: number;
      }
    ];
  };
}

const distanceOptions: WsSelectObj[] = [
  {
    label: 'Select',
    value: 0,
  },
  {
    label: '20 mi',
    value: 20,
  },
  {
    label: '30 mi',
    value: 30,
  },
  {
    label: '50 mi',
    value: 50,
  },
  {
    label: '100 mi',
    value: 100,
  },
  {
    label: '200 mi',
    value: 200,
  },
  {
    label: '500 mi',
    value: 500,
  },
  {
    label: '1000 mi',
    value: 1000,
  },
];

const mapLocationSize = new Map();
mapLocationSize.set(true, 9);
mapLocationSize.set(false, 12);

const geocodeByPlaceId = async (placeId: string) => {
  const geocoder = new (window as any).google.maps.places.PlacesService(
    document.createElement('div')
  );
  return new Promise(function (resolve) {
    geocoder.getDetails(
      { placeId, fields: ['geometry.location', 'address_components', 'place_id'] },
      async (place: any, status: any) => {
        if (status === 'OK') {
          resolve([place]);
        }
      }
    );
  });
};

export default function WsLocationSearch({
  required = false,
  placeHolder = 'Search Location',
  size = 'small',
  multiSelect = true,
  showDistance = false,
  distanceSelectName = 'distance',
  showMandatorySymbol = false,
  variant = 'outlined',
  name,
  hideLabel = false,
  disabled = false,
  types = ['locality', 'administrative_area_level_1', 'postal_town', 'country', 'continent'],
  wisestepApi = false,
  token = 'token',
  authorization = 'authorization',
}: ILocationSearchProps) {
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<PlaceType[]>([]);
  const loaded = React.useRef(false);
  const [wsApiCall, setWSApiCall] = useState<boolean>(false);
  const [isLocationListOpen, setLocationListOpen] = useState<boolean>(false);

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyDNqpfhvGZyzc0Ffc2MCs70hnpk-IIsl0c&libraries=places',
        document.querySelector('head'),
        'google-maps'
      );
    }
    loaded.current = true;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const fetchdata = React.useMemo(
    () =>
      throttle(
        (
          request: { input: string; types: string[] },
          callback: (results?: PlaceType[]) => void
        ) => {
          const url = process.env.REACT_APP_WS_LOCATION_API_ENDPOINT + request.input;
          if (wisestepApi && !wsApiCall) {
            fetch(url ?? '', {
              method: 'GET',
            })
              .then((response) => response.json())
              .then((data) => callback(data));
          } else {
            (autocompleteService.current as any).getPlacePredictions(request, callback);
          }
        },
        200
      ),
    [wisestepApi, wsApiCall]
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && (window as any).google) {
      autocompleteService.current = new (window as any).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions([]);
      return undefined;
    }

    fetchdata(
      {
        input: inputValue,
        types,
      },
      (results?: PlaceType[]) => {
        if ((active && !wisestepApi) || wsApiCall) {
          setOptions(results || []);
        } else {
          setOptions(parseWSlocationData(results) || []);
        }
      }
    );

    return () => {
      active = false;
    };
  }, [inputValue, fetchdata, multiSelect, size, wisestepApi, wsApiCall]);

  const parseWSlocationData = (results?: any[]) => {
    const noOptionFound = [
      {
        id: '',
        area: null,
        keywords: ['★ ★ ★ 𝙉𝙤𝙩 𝙖𝙗𝙡𝙚 𝙩𝙤 𝙛𝙞𝙣𝙙 𝙮𝙤𝙪𝙧 𝙡𝙤𝙘𝙖𝙩𝙞𝙤𝙣? 𝘾𝙡𝙞𝙘𝙠 𝙝𝙚𝙧𝙚 ★ ★ ★'],
        city: null,
        state: null,
        stateCode: null,
        country: null,
        countryCode: null,
        continent: null,
        continentCode: null,
        zipCode: null,
        lat: 0,
        lon: 0,
        point: null,
        locationType: null,
        isInternal: true,
        place_id: null,
        description: null,
      },
    ];
    results?.push(noOptionFound[0]);
    const locationList: PlaceType[] = [];

    results?.forEach((element) => {
      const location: PlaceType = {
        place_id: '',
        description: '',
        structured_formatting: {
          main_text: '',
          secondary_text: '',
          main_text_matched_substrings: [{ offset: 0, length: 0 }],
        },
      };
      location.place_id = element.id;
      location.description = element.keywords.toString();
      locationList.push(location);
    });

    return locationList;
  };

  const placeDataMapper = (placeData: any) => {
    const mappedLocation: ILocation = {
      area: placeData.area ?? '',
      city: placeData.city ?? '',
      continent: placeData.continent ?? '',
      continentCode: placeData.continentCode ?? '',
      country: placeData.country ?? '',
      countryCode: placeData.countryCode ?? '',
      id: placeData.id ?? '',
      internal: placeData.internal ?? '',
      keywords: placeData.keywords ?? [],
      label: placeData.label ?? '',
      lat: placeData.lat ?? '',
      lon: placeData.lon ?? '',
      point:
        placeData?.point || (placeData?.lat != null && placeData?.lon != null)
          ? `${placeData?.lat},${placeData?.lon}`
          : '',
      placeId: placeData.place_id ?? '',
      state: placeData.state,
      stateCode: placeData.stateCode,
      zipCode: placeData.zipCode,
    };

    return mappedLocation;
  };

  const getLocationData = async (location: any): Promise<ILocation | null> => {
    if (wisestepApi && !wsApiCall) {
      const url = process.env.REACT_APP_WS_LOCATION_INFO_API_ENDPOINT + location.place_id;
      const placeData = await fetch(url, { method: 'GET' }).then((response) => response.json());
      return placeDataMapper(placeData);
    } else {
      const place = (await geocodeByPlaceId(location.place_id)) as any[];
      const placeTOLocation = placeToLocation(place[0]);

      if (placeTOLocation != null) {
        pushLocationToWS(placeTOLocation);
      }

      return placeTOLocation;
    }
  };

  const pushLocationToWS = async (placeTOLocation: ILocation) => {
    const url = 'https://backend.wisestep.com/locationsapi/api/wisestep/location';
    await fetch(url ?? '', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': token,
        Authorization: authorization,
      },
      body: JSON.stringify(placeTOLocation),
      credentials: 'include',
    }).then((response) => response.json());
  };
  const { control, setValue, getValues } = useFormContext();
  React.useEffect(() => {
    const defaultValue = getValues(name);
    if (defaultValue == null) {
      setValue(name, multiSelect ? [] : null);
    }
  }, []);
  const onChange = (
    locationsData: PlaceType[],
    reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails
  ) => {
    const temp: any = details.option;
    if (reason === 'clear') {
      multiSelect ? setValue(name, []) : setValue(name, null);
    } else if (reason === 'removeOption') {
      removeOption(details);
    } else if (temp.place_id === null || temp.place_id === '') {
      if (!multiSelect)
        setValue(name, {
          area: '',
          city: '',
          state: '',
          stateCode: '',
          country: '',
          countryCode: '',
          continent: '',
          continentCode: '',
          zipCode: '',
          lat: 0,
          lon: 0,
          point: '',
          placeId: '',
        });
      const timeoutId = setTimeout(() => {
        setLocationListOpen(true);
      }, 50);
      () => clearTimeout(timeoutId);

      defaultLink(true);
    } else {
      setLocations(details, locationsData);
      defaultLink(false);
    }
  };

  const removeOption = (details: AutocompleteChangeDetails) => {
    const selectedLocations = getValues(name) ?? [];
    const index = selectedLocations.indexOf(details.option);
    selectedLocations.splice(index, 1);
    setValue(name, selectedLocations);
  };

  const defaultLink = (value: boolean) => {
    setWSApiCall(value);
  };

  const setLocations = (details: AutocompleteChangeDetails, locationsData: PlaceType[]) => {
    if (multiSelect) {
      const selectedLocations: ILocation[] = getValues(name) ?? [];
      if (!details.option) {
        const setLocationData = () => {
          locationsData?.map(async (location: any) => {
            const locationData = await getLocationData(location);
            if (locationData) {
              selectedLocations.push(locationData);
            }
            setValue(name, selectedLocations);
          });
        };
        setLocationData();
      } else {
        const setLocations = async () => {
          const locationData = await getLocationData(details.option);
          if (locationData) {
            selectedLocations.push(locationData);
          }
          setValue(name, selectedLocations);
        };
        setLocations();
      }
    } else {
      const setLocationData = async () => {
        const locationData = await getLocationData(locationsData);
        if (locationData) {
          setValue(name, locationData);
        }
      };
      setLocationData();
    }
  };

  const getOptionTags = (
    data: (ILocation & PlaceType)[],
    tagsProps: AutocompleteRenderGetTagProps
  ) =>
    data.map((value, index) => {
      const { onDelete } = tagsProps({ index });
      if ((value.place_id != '' && value.place_id != null) || value.place_id === undefined) {
        return (
          <Chip
            key={index}
            sx={{
              margin: 0.1,
              padding: 0.1,
            }}
            label={
              value.description ||
              [value.city, value.state, value.country].filter((x) => x).toString() ||
              value.continent
            }
            onDelete={onDelete}
          />
        );
      } else return null;
    });

  const createLocationString = (city: string, state: string, country: string) => {
    const parts = [];
    city && parts.push(city);
    country && parts.push(country);
    state && parts.push(state);
    return parts.join(', ');
  };

  return (
    <Grid container spacing={2} display="flex" flexDirection="row">
      <Grid item xs={mapLocationSize.get(showDistance)}>
        {!hideLabel && (
          <Label>
            {placeHolder}
            {showMandatorySymbol ? ' *' : ''}
          </Label>
        )}
        <Controller
          name={name}
          control={control}
          defaultValue={multiSelect ? [] : null}
          render={({ field: { value, ref }, fieldState: { error, invalid } }) => (
            <>
              <Autocomplete
                onClose={() => {
                  setLocationListOpen(false);
                }}
                onOpen={() => {
                  setLocationListOpen(true);
                  setOptions([]);
                }}
                open={isLocationListOpen}
                handleHomeEndKeys
                disabled={disabled}
                id="google-map-demo"
                size={size}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : option.description
                    ? option.description
                    : option.city || option.state || option.country
                    ? createLocationString(option.city, option.state, option.country)
                    : ''
                }
                filterOptions={(x) => x}
                options={options}
                autoComplete
                fullWidth
                multiple={multiSelect}
                includeInputInList
                isOptionEqualToValue={(option, value) =>
                  option.place_id === (value.placeId || value.place_id)
                }
                limitTags={2}
                getLimitTagsText={(number) => (
                  <Chip
                    sx={{
                      margin: 0.1,
                      padding: 0.1,
                    }}
                    label={`+ ${number}`}
                  />
                )}
                renderTags={(data, tagsProps) => getOptionTags(data, tagsProps)}
                value={value}
                onChange={(
                  event: React.ChangeEvent<any>,
                  value: any,
                  reason: AutocompleteChangeReason,
                  details: AutocompleteChangeDetails
                ) => onChange(value, reason, details)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={placeHolder}
                    variant={variant}
                    fullWidth
                    onChange={handleChange}
                    required={required}
                    error={invalid}
                    inputRef={ref}
                    {...omit(params, ['label'])}
                  />
                )}
              />
              {error && <FormHelperText error>{error.message}</FormHelperText>}
            </>
          )}
        />
      </Grid>
      {showDistance && (
        <Grid item xs={3}>
          <WsSelect
            name={distanceSelectName}
            selectArray={distanceOptions}
            label="Radius"
            size="small"
            defaultValue="0"
          />
        </Grid>
      )}
    </Grid>
  );
}

export interface ILocationSearchProps {
  required?: boolean;
  name: string;
  placeHolder: string;
  size?: 'small' | 'medium';
  multiSelect?: boolean;
  showDistance?: boolean;
  distanceSelectName?: string;
  showMandatorySymbol?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  hideLabel?: boolean;
  disabled?: boolean;
  types?: string[];
  wisestepApi?: boolean;
  token?: string;
  authorization?: string;
}
